import MercedesLogo from '@/common/components/lottie/mercedes-logo';
import Spacer from '@/common/components/spacer';
import FlexcarLogoWhite from '@/icons/flexcar-logo-white';
import CancelWithCircle from '@/icons/cancel-with-circle';
import { Button } from '@/shadcn-ui/button';
import useIsClient from '@/util-react-router/hooks/use-is-client';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import Rule from '@/common/components/rule';
import { colors } from '@/theme/colors';

interface MercedesInventoryBannerProps {
  PUBLIC_CDN_URL: string;
}

export default function MercedesInventoryBanner({
  PUBLIC_CDN_URL,
}: MercedesInventoryBannerProps) {
  const [isClosed, setIsClosed] = useState<boolean>(true);
  const isClient = useIsClient();

  const closeBanner = useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
    // Need both preventDefault and stopPropagation to prevent the link from being followed when close button is clicked
    event?.preventDefault();
    event?.stopPropagation();
    sessionStorage?.setItem('mercedes-inventory-banner', 'true');
    setIsClosed(true);
  }, []);

  useEffect(() => {
    if (isClient) {
      setIsClosed(sessionStorage?.getItem('mercedes-inventory-banner') === 'true');
    }
  }, [isClient]);

  if (isClosed) return null;

  return (
    <Link
      className="relative flex w-full flex-row gap-8 rounded-[24px] p-[24px]"
      to="https://www.flexcar.com/vehicles/2025-mercedes"
      reloadDocument={true}
    >
      <div className="z-1 flex w-full flex-col items-center">
        <div className="flex items-center justify-center gap-[16px]">
          <MercedesLogo
            className="h-[36px] w-[36px] md:h-[40px] md:w-[40px]"
            loop={false}
          />
          <Rule
            direction="vertical"
            color="bg-flexcar-neutral100"
            className="h-[20px] rounded-full opacity-60"
          />
          <FlexcarLogoWhite className="h-[36px] w-[36px] md:h-[40px] md:w-[40px]" />
        </div>
        <Spacer variant="height8" />
        <h1 className="text-[28px] font-semibold leading-[40px] tracking-[-1px] text-flexcar-neutral100 md:text-[32px] md:leading-[44px]">
          Luxury Unleashed
        </h1>
        <Spacer variant="height16" />
        <Button
          variant="secondary"
          className="body2-strong h-[36px] w-full max-w-[280px] rounded-[11px] border-none px-[24px] py-[12px] text-[14px] leading-[20px] ring-0 md:w-[180px]"
        >
          Learn more
        </Button>
      </div>
      <Button
        variant="clear"
        onClick={closeBanner}
        className="absolute right-[16px] top-[16px] z-10 h-fit p-0 md:right-[24px] md:top-[24px]"
      >
        <CancelWithCircle
          className="ml-auto h-[24px] w-[24px] cursor-pointer"
          circleClassName="fill-flexcar-neutral100/10 group-hover:fill-flexcar-neutral100/20 group-active:fill-flexcar-neutral100/30"
          fillPrimary={colors.neutral100}
        />
      </Button>
      <img
        src={`${PUBLIC_CDN_URL}/web/images/mercedes-background-gradient.webp`}
        alt="Mercedes inventory banner background gradient"
        className="absolute inset-0 z-0 h-full w-full rounded-[24px] object-cover object-left md:object-center"
      />
    </Link>
  );
}
